import { useEffect, useState } from "react";
import { Calendar, Clock, Phone, MessageSquare, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";

function groupByDay(items) {
    const groups = {};
    for (const a of items) {
        const key = (a.slot_label || "").split(" · ")[0] || "Other";
        if (!groups[key]) groups[key] = [];
        groups[key].push(a);
    }
    return groups;
}

export default function AppointmentsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/appointments", { params: { upcoming_only: true } });
            setItems(data.items || []);
        } catch (e) {
            toast.error("Failed to load appointments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const changeStatus = async (id, status) => {
        try {
            await http.patch(`/appointments/${id}`, { status });
            setItems((prev) => prev.filter((a) => a.id !== id));
            toast.success(`Marked ${status}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const groups = groupByDay(items);
    const dayKeys = Object.keys(groups);

    return (
        <div className="card-surface p-0 overflow-hidden" data-testid="appointments-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-emerald-300" />
                    <h3 className="text-white font-semibold">Upcoming calls</h3>
                    <span className="chip ml-2">{items.length}</span>
                </div>
                <button onClick={load} className="btn-ghost !py-1.5 !min-h-0 text-xs" data-testid="appts-refresh-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>
            <div className="p-5">
                {items.length === 0 ? (
                    <div className="text-sm text-slate-400 py-6 text-center" data-testid="appts-empty">
                        No upcoming calls booked. When a lead picks a slot on the thank-you page it shows up here.
                    </div>
                ) : (
                    <div className="space-y-5">
                        {dayKeys.map((day) => (
                            <div key={day}>
                                <div className="text-xs mono text-slate-400 uppercase tracking-widest">{day}</div>
                                <ul className="mt-2 space-y-2">
                                    {groups[day].map((a) => (
                                        <li
                                            key={a.id}
                                            data-testid={`appt-row-${a.id}`}
                                            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 text-white font-semibold">
                                                    <Clock className="w-4 h-4 text-emerald-300" />
                                                    {(a.slot_label || "").split(" · ")[1] || a.slot_label}
                                                    {a.reminder_sent_at && (
                                                        <span className="chip !bg-blue-500/10 !border-blue-500/30 !text-blue-200">reminded</span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-slate-300 mt-0.5 truncate">
                                                    {a.name} · {a.phone}
                                                    {a.email ? <span className="text-slate-500"> · {a.email}</span> : null}
                                                </div>
                                                {a.notes && (
                                                    <div className="mt-1 text-xs text-slate-400 italic">"{a.notes}"</div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <a href={`tel:${a.phone}`} className="btn-ghost !py-1.5 !min-h-0 text-xs" data-testid={`appt-call-${a.id}`}>
                                                    <Phone className="w-3.5 h-3.5" />
                                                </a>
                                                <a href={`sms:${a.phone}`} className="btn-ghost !py-1.5 !min-h-0 text-xs" data-testid={`appt-text-${a.id}`}>
                                                    <MessageSquare className="w-3.5 h-3.5" />
                                                </a>
                                                <button
                                                    onClick={() => changeStatus(a.id, "completed")}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-emerald-500/40 text-emerald-200 text-xs hover:bg-emerald-500/10"
                                                    data-testid={`appt-complete-${a.id}`}
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5" /> Done
                                                </button>
                                                <button
                                                    onClick={() => changeStatus(a.id, "cancelled")}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/10 text-slate-300 text-xs hover:bg-white/5"
                                                    data-testid={`appt-cancel-${a.id}`}
                                                >
                                                    <XCircle className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
